import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { listListingStays, unarchiveStay } from '../../../../api/stays-reservations-client';
import type { StayChip, StayFull, StaysListingHead, StaysPolicy, StayTable } from '../../../../interfaces/stays';
import { AppButton } from '../../../button/app-button';
import { ChipGroup, ToggleChip } from '../../../toggle-chip/toggle-chip';
import { ClockNote } from '../../../clock-note/clock-note';
import { Field, Input, Label } from '../../../form-kit';
import { ListSkeleton, LoadError, skeletonHeight } from '../../dashboard/dashboard-shared-components/load-state';
import { Presence, Staleable, StateSwitch } from '../../../../motion';
import { Table, TableCaption, TableFrame, Tbody, Thead } from '../../../table/list-table';
import toast from '../../../../utils/toast';
import { space, spacing } from '../../../../style-utils/theme';
import { Empty, EmptyText } from '../../dashboard/dashboard-stays-components/dashboard-stays.styled';
import { EditReservationModal } from '../reservation-form/edit-reservation-modal';
import { buildingClock } from '../stays-reservations-model';
import { listingSectionHref } from '../listing/stays-listing-routes';
import { CHIPS, chipText, firstOf, isUpcoming } from './reservation-labels';
import { ReservationHeadRow, ReservationRow, reservationColumns } from './reservation-row';
import { TurnaroundRow } from './turnaround-row';
import { reportStayError } from './reservation-actions-menu';
import { getOperationsToday, OperationsToday } from '../../../../api/stays-alerts-client';
import { CleaningTodayBanner } from '../operations/cleaning-today-banner';
import { CancellationsTodayCard } from '../operations/cancellations-today-card';
import { ExtrasDueSlot } from '../operations/extras-due-slot';
import { TurnaroundBridge } from '../operations/turnaround-bridge';
import { MessageComposer } from '../messages/message-composer';

/**
 * THE STAYS TABLE (plan S4, R-S4.24–R-S4.26) — /dashboard/stays/<id>/stays.
 *
 * Search is SERVER-side (`q`: name, email, phone, source; debounced), the
 * stage chips and "Show archived" are server filters too, and the counts on
 * the chips are the response's. The server sorts — a revoked stay keeps its
 * place — and pages with `next_cursor` ("Show more" appends).
 *
 * The clock facts come from the response's envelope: building_now drives the
 * journey bars, building_today the Archive rule, building_zone the one
 * ClockNote. /dashboard/stays/<id>/stays/<reservation id> opens that stay's
 * edit modal over the table (a stable deep link); closing it returns the
 * address to the table.
 *
 * Plan S8 (SC-2) adds, from ONE operations-today read refreshed with the
 * table: the cleaning-today banner and the cancelled-today card above it, the
 * extras slot (empty until S10), S8's turnaround bridge where the server has
 * the pair (S4's row otherwise), the cleaning pill in each live row and the
 * "Message" row action (the composer).
 */

const PAGE = 50;
const MAX_LIMIT = 500;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const ToolbarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${spacing.sm};
  min-width: 0;
`;

/* The form-kit Input is already 16px (no iOS zoom on focus) and 48px tall. */
const SearchField = styled(Field)`
  flex: 1 1 ${space(30)};
  min-width: 0;
  max-width: ${space(60)};
  margin: 0;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  min-width: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

const More = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${spacing.xs};
`;

const rowsOf = (t: StayTable): StayFull[] =>
  (Array.isArray(t.rows) && t.rows) || (Array.isArray(t.items) && t.items) || [];

/** Page 2 appended to page 1, by id (a row that moved between reads is shown once). */
const appendRows = (current: StayFull[], next: StayFull[]): StayFull[] => {
  const seen = new Set(current.map(r => r.id));

  return [...current, ...next.filter(r => !seen.has(r.id))];
};

type Editing = { id: string; focus: 'money' | null; fromUrl: boolean };

export interface StaysReservationsTableProps {
  listing: StaysListingHead;
  policy?: StaysPolicy;
  /** stays:manage — Edit, the menu, the paid toggle, Add amounts, Undo archive. */
  canManage: boolean;
  /** From the address: open this stay's edit modal. */
  reservationId?: string | null;
}

export const StaysReservationsTable: React.FC<StaysReservationsTableProps> = ({
  listing,
  policy,
  canManage,
  reservationId = null,
}) => {
  const navigate = useNavigate();
  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [chip, setChip] = useState<StayChip>('all');
  const [archived, setArchived] = useState(false);

  const [table, setTable] = useState<StayTable | null>(null);
  const [rows, setRows] = useState<StayFull[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'error' | 'ready'>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [undo, setUndo] = useState<StayFull | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(
    reservationId ? { id: reservationId, focus: null, fromUrl: true } : null
  );
  const [ops, setOps] = useState<OperationsToday | null>(null);
  const [messaging, setMessaging] = useState<StayFull | null>(null);

  const seq = useRef(0);
  const hasData = useRef(false);
  const rowsRef = useRef<StayFull[]>([]);
  rowsRef.current = rows;

  // The search is sent ~300ms after the last keystroke, never per key.
  useEffect(() => {
    const t = setTimeout(() => setQ(qText.trim()), 300);

    return () => clearTimeout(t);
  }, [qText]);

  useEffect(() => {
    if (reservationId) setEditing({ id: reservationId, focus: null, fromUrl: true });
  }, [reservationId]);

  /** `keep`: re-read as many rows as are showing (after a write). */
  const load = useCallback(
    async (keep = false) => {
      seq.current += 1;
      const mine = seq.current;
      if (hasData.current) setRefreshing(true);
      const limit = keep ? Math.min(MAX_LIMIT, Math.max(PAGE, rowsRef.current.length)) : PAGE;
      try {
        const t = await listListingStays(listing.id, { q, stage: chip, includeArchived: archived, limit });
        if (seq.current !== mine) return;
        hasData.current = true;
        setTable(t);
        setRows(rowsOf(t));
        setCursor(t.next_cursor || null);
        setRefreshFailed(false);
        setPhase('ready');
      } catch {
        if (seq.current !== mine) return;
        if (hasData.current) setRefreshFailed(true);
        else setPhase('error');
      } finally {
        if (seq.current === mine) setRefreshing(false);
      }
    },
    [listing.id, q, chip, archived]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Plan S8: today's operations (a failure leaves the table as S4 had it).
  const loadOps = useCallback(async () => {
    try {
      setOps(await getOperationsToday(listing.id));
    } catch {
      setOps(null);
    }
  }, [listing.id]);

  useEffect(() => {
    void loadOps();
  }, [loadOps]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const t = await listListingStays(listing.id, { q, stage: chip, includeArchived: archived, limit: PAGE, cursor });
      if (seq.current !== mine) return;
      setRows(current => appendRows(current, rowsOf(t)));
      setCursor(t.next_cursor || null);
    } catch {
      if (seq.current === mine) toast.error('Could not load more stays. Try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  /** A row (or the modal) wrote: show it at once, then read the list again. */
  const changedFor = (id: string) => (next: StayFull | null) => {
    setRows(current => (next ? current.map(r => (r.id === id ? next : r)) : current.filter(r => r.id !== id)));
    void load(true);
    void loadOps();
  };

  const undoArchive = async () => {
    if (!undo || undoing) return;
    setUndoing(true);
    try {
      await unarchiveStay(undo.id, undo.version);
      toast.success(`${firstOf(undo)}'s stay is back from the archive.`);
      setUndo(null);
    } catch (error) {
      reportStayError(error, 'Could not undo the archive. Try again.');
    } finally {
      setUndoing(false);
      void load(true);
    }
  };

  const closeEdit = () => {
    const fromUrl = editing?.fromUrl;
    setEditing(null);
    if (fromUrl) navigate(listingSectionHref(listing.id, 'stays'), { replace: true });
  };

  const counts = table?.counts;
  const buildingNow = table?.building_now || '';
  const buildingToday = table?.building_today || table?.today || '';
  const zone = table?.building_zone || table?.listing?.timezone || listing.timezone;
  const head = table?.listing || listing;
  const columns = reservationColumns(canManage);
  const firstUpcoming = rows.find(isUpcoming)?.id || null;

  const emptyText =
    counts && counts.all === 0 && !q && !archived
      ? 'No stays yet. Add the first one with New stay.'
      : q
      ? `No stays match "${q}".`
      : 'No stays in this view.';

  const view: 'loading' | 'error' | 'empty' | 'ready' =
    phase === 'loading' ? 'loading' : phase === 'error' ? 'error' : rows.length ? 'ready' : 'empty';

  /* Rows, with a turnaround bridge between a departure and its same-day
     arrival: between the two when they sit side by side, else right after the
     departure. Keyed elements in one flat list, so Presence can see each. */
  const body: React.ReactElement[] = [];
  const byId = new Map(rows.map(r => [r.id, r]));
  const bridged = new Set<string>();
  const opsPair = (outId: string, inId: string) =>
    ops ? ops.turnarounds.find(p => p.departing_id === outId && p.arriving_id === inId) : undefined;
  const bridge = (out: StayFull, inn: StayFull, index: number) => {
    const t = out.turnaround;
    if (!t || bridged.has(out.id)) return;
    bridged.add(out.id);
    const pair = opsPair(out.id, inn.id);
    if (pair) {
      body.push(
        <TurnaroundBridge key={`turn-${out.id}`} pair={pair} departing={out} arriving={inn} columns={columns} index={index} />
      );

      return;
    }
    body.push(
      <TurnaroundRow
        key={`turn-${out.id}`}
        departing={out}
        arriving={inn}
        date={t.date}
        hours={t.window_hours}
        columns={columns}
        index={index}
      />
    );
  };
  rows.forEach((row, i) => {
    const index = i % PAGE;
    const prev = i > 0 ? rows[i - 1] : null;
    // The arrival listed right after its departure, or the departure right after its arrival.
    if (prev && prev.turnaround && prev.turnaround.with_reservation_id === row.id) bridge(prev, row, index);
    if (prev && row.turnaround && row.turnaround.with_reservation_id === prev.id) bridge(row, prev, index);
    body.push(
      <ReservationRow
        key={row.id}
        stay={row}
        index={index}
        buildingNow={buildingNow}
        buildingToday={buildingToday}
        canManage={canManage}
        nextArrival={row.id === firstUpcoming}
        onEdit={canManage ? (s, focus) => setEditing({ id: s.id, focus: focus || null, fromUrl: false }) : undefined}
        onChanged={changedFor(row.id)}
        onArchived={s => setUndo(s)}
        onMessage={canManage ? s => setMessaging(s) : undefined}
      />
    );
    const next = i + 1 < rows.length ? rows[i + 1] : null;
    const partner = row.turnaround ? byId.get(row.turnaround.with_reservation_id) : undefined;
    if (partner && partner !== next && partner !== prev) bridge(row, partner, index);
  });

  return (
    <Section aria-label="Stays" data-testid="stays-table">
      {ops ? (
        <>
          <CleaningTodayBanner
            items={ops.cleaning_today}
            canManage={canManage}
            onChanged={() => {
              void loadOps();
              void load(true);
            }}
          />
          <CancellationsTodayCard
            count={ops.cancelled_today.count}
            items={ops.cancelled_today.items}
            onView={() => setChip('cancelled')}
          />
          <ExtrasDueSlot extras={ops.extras_due} />
        </>
      ) : null}
      <Toolbar>
        <ToolbarRow>
          <SearchField>
            <Label htmlFor={`stays-search-${listing.id}`}>Search stays</Label>
            <Input
              id={`stays-search-${listing.id}`}
              type="search"
              value={qText}
              autoComplete="off"
              placeholder="Name, email, phone or source"
              maxLength={80}
              onChange={e => setQText(e.target.value)}
            />
          </SearchField>
          {canManage && undo ? (
            <AppButton variant="secondary" loading={undoing} loadingLabel="Undoing the archive" onClick={() => void undoArchive()}>
              {`Undo archive: ${firstOf(undo)}`}
            </AppButton>
          ) : null}
        </ToolbarRow>
        <Filters>
          <ChipGroup aria-label="Show">
            {CHIPS.map(c => (
              <ToggleChip key={c.key} pressed={chip === c.key} onToggle={() => setChip(c.key)}>
                {chipText(c.label, counts ? counts[c.key] : undefined)}
              </ToggleChip>
            ))}
          </ChipGroup>
          <ToggleChip pressed={archived} onToggle={next => setArchived(next)}>
            {chipText('Show archived', counts ? counts.archived : undefined)}
          </ToggleChip>
        </Filters>
        <ClockNote clock={buildingClock(zone)} />
      </Toolbar>

      {refreshFailed ? <LoadError message="Couldn't refresh the stays." onRetry={() => void load(true)} /> : null}

      <StateSwitch state={view} minHeight={skeletonHeight(4, 96)}>
        {view === 'loading' ? (
          <ListSkeleton rows={4} rowHeight={96} label="stays" />
        ) : view === 'error' ? (
          <LoadError message="Couldn't load the stays" onRetry={() => void load(false)} />
        ) : view === 'empty' ? (
          <Staleable stale={refreshing}>
            <Empty role="status">
              <EmptyText>{emptyText}</EmptyText>
            </Empty>
          </Staleable>
        ) : (
          <Staleable stale={refreshing}>
            <TableFrame>
              <Table data-stack="lg">
                <TableCaption>Stays at {head.name}</TableCaption>
                <Thead>
                  <ReservationHeadRow canManage={canManage} />
                </Thead>
                <Tbody>
                  <Presence>{body}</Presence>
                </Tbody>
              </Table>
            </TableFrame>
            {cursor ? (
              <More>
                <AppButton variant="secondary" loading={loadingMore} loadingLabel="Loading more stays" onClick={() => void loadMore()}>
                  Show more
                </AppButton>
              </More>
            ) : null}
          </Staleable>
        )}
      </StateSwitch>

      {canManage ? (
        <EditReservationModal
          reservationId={editing ? editing.id : null}
          listing={head}
          policy={policy}
          focus={editing ? editing.focus : null}
          onClose={closeEdit}
          onSaved={next => {
            if (editing) changedFor(editing.id)(next);
          }}
        />
      ) : null}
      {canManage ? <MessageComposer stay={messaging} onClose={() => setMessaging(null)} /> : null}
    </Section>
  );
};
